import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Button,
  FormControl,
  Input,
  ModalBody,
  ModalFooter,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import 'react-datepicker/dist/react-datepicker.css';
import {
  CreateShipmentRequest,
  Shipment,
  shipmentsApi,
} from '@texas/api/endpoints/shipmentsApi';
import { User, userApi } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { reactSelectStyle, reactSelectTheme } from '@texas/resources/styles';
import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { useForm, Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import { request } from '@texas/utils/helpers/httpHelpers';
import { onlyUnique } from '@texas/utils/helpers/commonHelpers';
import { SharedDisclosureProps } from '../../types';
import { shipmentEvents } from '../events';
import { defaultCouriers } from '../types';
import { useTranslation } from 'react-i18next';
import { TransportRequestsSummary } from '../TransportRequestsSummary';
import { FormCounter } from '../../form/FormCounter';

interface Props extends SharedDisclosureProps {
  onCreate: (data: Shipment) => void;
  toUserId?: number;
  ticketNumber: number;
  samplesCount: number;
  defaultTrackingNumber?: string;
  requestedDispatchDate: string;
}

const maxLength = 60;

export function AddInternalShipmentModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={0}>{t('shipment.new')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          defaultTrackingNumber={props.defaultTrackingNumber}
          requestedDispatchDate={props.requestedDispatchDate}
          toUserId={props.toUserId}
          ticketNumber={props.ticketNumber}
          samplesCount={props.samplesCount}
          onCreate={(data) => {
            props.onCreate(data);
            shipmentEvents.onShipmentCreated.dispatch();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  onCreate,
  toUserId,
  ticketNumber,
  samplesCount,
  defaultTrackingNumber,
  requestedDispatchDate,
}: {
  onCreate: (data: Shipment) => void;
  toUserId?: number;
  ticketNumber: number;
  samplesCount: number;
  defaultTrackingNumber?: string;
  requestedDispatchDate: string;
}) {
  const { t } = useTranslation();
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    watch,
  } = useForm<CreateShipmentRequest>({
    defaultValues: {
      receiverUserId: toUserId,
      trackingNumber: defaultTrackingNumber ?? '',
      dispatchDate: new Date(),
      note: '',
      courier: '',
    },
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { colorMode } = useColorMode();

  const {
    data: users,
    refetch: refetchUsers,
    loading: loadingUsers,
  } = useApiResource(userApi.getAllInternal);

  const {
    data: couriers,
    refetch: refetchCouriers,
    loading: loadingCouriers,
  } = useApiResource(shipmentsApi.getCouriers);

  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  useEffect(() => {
    refetchCouriers(selectedUser?.branchId ?? null);
  }, [refetchCouriers, selectedUser?.branchId]);

  const { request: createRequest, error: createError } = useApiRequest(
    shipmentsApi.createShipment,
  );

  const performRequest = (data: CreateShipmentRequest) =>
    request(createRequest, [data], (res) => onCreate(res));
  return (
    <>
      <form onSubmit={handleSubmit(performRequest)}>
        <ModalBody>
          {createError && <ErrorDetails error={createError} />}

          <Flex gap={4} flexDir="column">
            <TransportRequestsSummary
              ticketNumbers={[ticketNumber]}
              samplesCount={samplesCount}
            />
            <FormControl>
              <TexasFormLabel>{t('shipment.whoWillRecieve')}</TexasFormLabel>
              <Controller
                control={control}
                name="receiverUserId"
                render={({ field }) => (
                  <TexasSelect
                    isLoading={loadingUsers}
                    value={{
                      value: field.value,
                      label: users?.find((u) => u.id === field.value)?.name,
                    }}
                    onChange={(e) => {
                      setSelectedUser(
                        users?.find((x) => x.id === e?.value) ?? null,
                      );
                      field.onChange(e?.value);
                    }}
                    options={users?.map((u) => ({
                      label: u.name,
                      value: u.id,
                    }))}
                  />
                )}
              />
              <ErrorLabel text={errors.receiverUserId?.message} />
            </FormControl>
            <FormControl isRequired={true}>
              <TexasFormLabel>{t('shipment.provideCourier')}</TexasFormLabel>
              <Controller
                control={control}
                name="courier"
                render={({ field }) => (
                  <CreatableSelect
                    styles={reactSelectStyle<false>({ mode: colorMode })}
                    theme={(theme) => reactSelectTheme(theme, colorMode)}
                    isLoading={loadingCouriers}
                    value={
                      field.value
                        ? {
                            value: field.value,
                            label: field.value,
                          }
                        : null
                    }
                    onChange={(e) => {
                      field.onChange(e?.label);
                    }}
                    options={defaultCouriers
                      .concat(couriers ?? [])
                      .filter(onlyUnique)
                      .map((x) => ({
                        label: x,
                        value: x,
                      }))}
                    placeholder={t('shipment.selectOrCreateNew')}
                  />
                )}
              />
              <ErrorLabel text={errors.courier?.message} />
            </FormControl>

            <FormControl isRequired={true}>
              <TexasFormLabel>{t('shipment.trackingNumber')}</TexasFormLabel>
              <Input
                {...register('trackingNumber', { maxLength })}
                variant="texas"
                placeholder={t('shipment.enterTrackingCode')}
              />
              <FormCounter
                length={watch('trackingNumber').length || 0}
                maxLength={maxLength}
              />
              <ErrorLabel text={errors.trackingNumber?.message} />
            </FormControl>
            <FormControl isRequired={true}>
              <TexasFormLabel>{t('shipment.dispatchDate')}</TexasFormLabel>
              <Text variant="small" pb={1}>
                {t('transportRequest.reqDispatchDateWithDate', {
                  date: requestedDispatchDate,
                })}
              </Text>
              <Controller
                control={control}
                name="dispatchDate"
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    inline={true}
                    calendarStartDay={1}
                  />
                )}
              />
              <ErrorLabel text={errors.dispatchDate?.message} />
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting || !isValid}
          >
            {t('general.create')}
          </Button>
        </ModalFooter>
      </form>
    </>
  );
}
